import { useState, useEffect } from 'react'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { useAuth } from '../../../context/AuthContext'
import { adminGetTeam, adminRemoveTeamMember, adminGetInvites, adminUpdateTeamMember, adminSetCptPermission } from '../../../services/admin'

function EditAdminModal({ admin, onClose, onSaved }) {
  const isOwner = admin.role === 'super_admin'
  const [isVerified, setIsVerified] = useState(admin.isVerified ?? false)
  const [isSuspended, setIsSuspended] = useState(admin.isSuspended ?? false)
  const [fullName, setFullName] = useState(admin.profile?.fullName ?? '')
  const [username, setUsername] = useState(admin.profile?.username ?? '')
  const [bio, setBio] = useState(admin.profile?.bio ?? '')
  const [avatarUrl, setAvatarUrl] = useState(admin.profile?.avatarUrl ?? '')
  const [twitter, setTwitter] = useState(admin.profile?.socialLinks?.twitter ?? '')
  const [linkedin, setLinkedin] = useState(admin.profile?.socialLinks?.linkedin ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    setLoading(true)
    setError(null)
    try {
      await adminUpdateTeamMember(admin.id, {
        isVerified,
        isSuspended,
        profile: {
          fullName, username, bio, avatarUrl,
          socialLinks: { twitter, linkedin },
        },
      })
      onSaved()
    } catch (err) {
      setError(err.message ?? 'Failed to update admin')
    } finally {
      setLoading(false)
    }
  }

  const Toggle = ({ label, checked, onChange, disabled }) => (
    <label className={`flex items-center gap-3 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <div
        onClick={() => !disabled && onChange(!checked)}
        className={`w-10 h-5 border-2 border-on-background relative transition-colors ${checked ? 'bg-primary-container' : 'bg-surface-container'}`}
      >
        <div className={`absolute top-0.5 w-3.5 h-3.5 bg-on-background transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </div>
      <span className="font-bold text-[12px] uppercase tracking-widest">{label}</span>
    </label>
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="bg-surface border-4 border-on-background w-full max-w-md flex flex-col gap-5 p-6"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Edit Admin</h2>
          <button onClick={onClose} className="p-1 hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {error && <AdminAlert type="error" message={error} />}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 border-b-2 border-on-background/10 pb-4">
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Account Status</p>
            <Toggle label="Verified" checked={isVerified} onChange={setIsVerified} disabled={isOwner} />
            <Toggle label="Suspended" checked={isSuspended} onChange={setIsSuspended} disabled={isOwner} />
            {isOwner && (
              <p className="text-[11px] text-on-surface-variant flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">shield</span>
                Owner account status is protected and can't be changed here.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Profile</p>
            {[
              { label: 'Full Name', value: fullName, set: setFullName, placeholder: 'John Doe' },
              { label: 'Username', value: username, set: setUsername, placeholder: 'johndoe' },
              { label: 'Avatar URL', value: avatarUrl, set: setAvatarUrl, placeholder: 'https://…' },
            ].map(({ label, value, set, placeholder }) => (
              <div key={label} className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</label>
                <input
                  type="text"
                  placeholder={placeholder}
                  value={value}
                  onChange={e => set(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
                />
              </div>
            ))}
            <div className="flex flex-col gap-1">
              <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Bio</label>
              <textarea
                rows={2}
                placeholder="Short bio"
                value={bio}
                onChange={e => setBio(e.target.value)}
                className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container resize-none"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Social Links</p>
            {[
              { label: 'Twitter', value: twitter, set: setTwitter, placeholder: 'https://twitter.com/…' },
              { label: 'LinkedIn', value: linkedin, set: setLinkedin, placeholder: 'https://linkedin.com/in/…' },
            ].map(({ label, value, set, placeholder }) => (
              <div key={label} className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</label>
                <input
                  type="text"
                  placeholder={placeholder}
                  value={value}
                  onChange={e => set(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function AdminTeam({ onInvite }) {
  const { user: me, isSuperAdmin } = useAuth()
  const [togglingId, setTogglingId] = useState(null)
  const [admins, setAdmins]     = useState([])
  const [invites, setInvites]   = useState([])
  const [tab, setTab]           = useState('active')
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const fetchAll = () => {
    setLoading(true)
    setError(null)
    Promise.allSettled([adminGetTeam(), adminGetInvites()])
      .then(([teamResult, inviteResult]) => {
        if (teamResult.status === 'fulfilled') {
          const d = teamResult.value
          const list = d?.data ?? d?.admins ?? d
          setAdmins(Array.isArray(list) ? list : [])
        } else {
          setError(teamResult.reason)
        }
        if (inviteResult.status === 'fulfilled') {
          const d = inviteResult.value
          const list = Array.isArray(d) ? d : (d?.invites ?? [])
          setInvites(list)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchAll() }, [])

  const handleRemove = async (adminId) => {
    try {
      await adminRemoveTeamMember(adminId)
      fetchAll()
    } catch (err) {
      setError(err)
    }
  }

  const handleToggleCpt = async (a) => {
    setTogglingId(a.id)
    setError(null)
    try {
      await adminSetCptPermission(a.id, !a.canDistributeCpt)
      setAdmins(list => list.map(x => x.id === a.id ? { ...x, canDistributeCpt: !a.canDistributeCpt } : x))
    } catch (err) {
      setError(err)
    } finally {
      setTogglingId(null)
    }
  }

  const statusDot = { online: 'bg-green-500', away: 'bg-yellow-400', offline: 'bg-on-surface-variant/30' }

  const initials = (name, email) => {
    const src = name?.trim() || email || ''
    const parts = src.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return src.slice(0, 2).toUpperCase()
  }
  const isRecentlyActive = (lastLogin) => {
    if (!lastLogin) return false
    return Date.now() - new Date(lastLogin).getTime() < 1000 * 60 * 30 // 30 min
  }
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—'

  return (
    <div className="flex flex-col gap-6">
      {editTarget && (
        <EditAdminModal
          admin={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null)
            setSuccessMsg('Admin updated successfully')
            setTimeout(() => setSuccessMsg(null), 4000)
            fetchAll()
          }}
        />
      )}
      {error && <AdminAlert type="error" message={error.message} />}
      {successMsg && <AdminAlert type="success" message={successMsg} />}

      <div className="flex items-center justify-between">
        <div className="flex gap-1 border-4 border-on-background">
          {['active', 'invited'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${tab === t ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
              {t === 'active' ? `Active (${admins.length})` : `Invited (${invites.length})`}
            </button>
          ))}
        </div>
        <button onClick={onInvite}
          className="flex items-center gap-2 px-5 py-3 bg-primary-container text-on-primary-fixed font-bold text-[11px] uppercase tracking-widest border-4 border-on-background hover:brightness-105 transition-all"
          style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
          <span className="material-symbols-outlined text-[16px]">person_add</span>
          Invite Admin
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : tab === 'active' ? (
          admins.length === 0 ? (
            <p className="text-[12px] text-on-surface-variant">No admins found.</p>
          ) : (
            admins.map((a, i) => {
              const name = a.profile?.fullName || a.email
              const status = a.isSuspended ? 'offline' : (isRecentlyActive(a.lastLogin) ? 'online' : 'away')
              const isOwner = a.role === 'super_admin'
              return (
                <div key={a.id ?? i} className="bg-surface border-4 border-on-background p-6 flex items-center gap-5"
                  style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
                  <div className="relative shrink-0">
                    <div className="w-12 h-12 bg-primary-container border-4 border-on-background flex items-center justify-center font-extrabold text-[13px] text-on-primary-fixed">
                      {initials(a.profile?.fullName, a.email)}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-surface ${statusDot[status]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-extrabold text-[15px]">{name}</p>
                      {isOwner ? (
                        <span className="flex items-center gap-1 font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-on-background bg-primary-container text-on-primary-fixed">
                          <span className="material-symbols-outlined text-[11px]">shield</span>
                          Owner
                        </span>
                      ) : (
                        <span className="font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-on-background/20 bg-surface-container text-on-surface-variant">{a.role}</span>
                      )}
                      {a.isSuspended && (
                        <span className="font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-error/40 bg-error-container text-on-error-container">Suspended</span>
                      )}
                    </div>
                    <p className="text-[12px] text-on-surface-variant">{a.email}</p>
                    <p className="text-[11px] text-on-surface-variant/60 mt-0.5">Since {fmtDate(a.createdAt)} · Last active {fmtDate(a.lastLogin)}</p>
                    {!isOwner && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => isSuperAdmin && handleToggleCpt(a)}
                          disabled={!isSuperAdmin || togglingId === a.id}
                          title={isSuperAdmin ? 'Allow or revoke CPT distribution for this admin' : 'Only the super admin can change this'}
                          className={`w-10 h-5 border-2 border-on-background relative transition-colors disabled:opacity-50 ${a.canDistributeCpt ? 'bg-primary-container' : 'bg-surface-container'} ${isSuperAdmin ? '' : 'cursor-not-allowed'}`}
                        >
                          <span className={`absolute top-0.5 w-3.5 h-3.5 bg-on-background transition-all ${a.canDistributeCpt ? 'left-[18px]' : 'left-0.5'}`} />
                        </button>
                        <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
                          Can distribute CPT
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {(isSuperAdmin || a.id === me?.id) && (
                    <button onClick={() => setEditTarget(a)} className="p-2 border-2 border-on-background hover:bg-primary-container hover:text-on-primary-container transition-colors">
                      <span className="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    )}
                    {isSuperAdmin && (
                    <button
                      onClick={() => !isOwner && handleRemove(a.id)}
                      disabled={isOwner}
                      title={isOwner ? "The owner account can't be removed" : 'Remove admin'}
                      className={`p-2 border-2 border-on-background transition-colors ${isOwner ? 'opacity-30 cursor-not-allowed' : 'hover:bg-error hover:text-on-error'}`}
                    >
                      <span className="material-symbols-outlined text-[18px]">{isOwner ? 'lock' : 'remove_circle'}</span>
                    </button>
                    )}
                  </div>
                </div>
              )
            })
          )
        ) : (
          invites.length === 0 ? (
            <p className="text-[12px] text-on-surface-variant">No pending invites.</p>
          ) : (
            invites.map((inv, i) => (
              <div key={inv.id ?? i} className="bg-surface border-4 border-on-background p-6 flex items-center gap-5"
                style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
                <div className="w-12 h-12 bg-surface-container border-4 border-on-background flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-on-surface-variant text-[20px]">schedule_send</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-extrabold text-[15px]">{inv.email}</p>
                  <p className="text-[11px] text-on-surface-variant/60 mt-0.5">
                    Invited {new Date(inv.createdAt).toLocaleDateString()} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-2 border-on-background/20 bg-surface-container text-on-surface-variant shrink-0">
                  Pending
                </span>
              </div>
            ))
          )
        )}
      </div>
    </div>
  )
}